import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';

export default function BotModeChoices({ onQuickChat, onWork, disabled = false, compact = false }) {
  const { colors } = useTheme();
  return (
    <View style={styles.options}>
      <ModeOption
        compact={compact}
        shortTitle="Chat"
        shortDescription="Talk it through. Uses fewer credits."
        title="Quick Chat With Bot"
        icon="comment-dots"
        eyebrow="CHAT · TALK IT THROUGH"
        description="Ask questions, get advice, or brainstorm together. Arya replies as your bot in a back-and-forth conversation."
        example="Uses fewer credits · Best for quick questions and everyday conversation."
        onPress={onQuickChat}
        disabled={disabled}
        colors={colors}
      />
      <ModeOption
        compact={compact}
        shortTitle="Give a job"
        shortDescription="Hand off complex tasks. Uses more credits."
        title="Give Bot A Job"
        icon="briefcase"
        eyebrow="WORK · HAND OFF A TASK"
        description="Describe the result you want. Your bot can research, use tools, and create files while you follow its progress."
        example="Uses more credits · Best for complex tasks with multiple steps."
        onPress={onWork}
        disabled={disabled}
        colors={colors}
      />
    </View>
  );
}

function ModeOption({ title, icon, eyebrow, description, example, onPress, disabled, colors, compact, shortTitle, shortDescription }) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityHint={`${description} ${example}`}
      disabled={disabled}
      onPress={onPress}
      style={({ hovered, pressed }) => [styles.option, {
        borderColor: hovered || pressed ? colors.border_color_primary : colors.border_color_secondary,
        backgroundColor: hovered || pressed ? colors.solid_color_secondary : colors.solid_color_tertiary,
        opacity: disabled ? 0.6 : 1,
      }]}
    >
      <View style={styles.optionHeader}>
        <View style={[styles.optionIcon, { backgroundColor: colors.solid_color_secondary }]}>
          <Icon name={icon} size={18} color={colors.text_color_primary} />
        </View>
        <View style={styles.optionHeading}>
          {!compact && <Text size="xxs" weight="semibold" color="tertiary" style={styles.eyebrow}>{eyebrow}</Text>}
          <Text weight="bold" style={styles.optionTitle}>{compact ? shortTitle : title}</Text>
          {compact && <Text size="s" color="secondary" style={styles.description}>{shortDescription}</Text>}
        </View>
        <Icon name="arrow-right" size={14} color={colors.text_color_secondary} />
      </View>
      {!compact && <>
        <Text size="s" color="secondary" style={styles.description}>{description}</Text>
        <Text size="xs" weight="semibold" color="secondary" style={styles.example}>{example}</Text>
      </>}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  options: { gap: 12 },
  option: { borderWidth: 1, borderRadius: 16, padding: 18, gap: 10 },
  optionHeader: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  optionIcon: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  optionHeading: { flex: 1, minWidth: 0, gap: 4 },
  optionTitle: { fontSize: 17, lineHeight: 23 },
  eyebrow: { letterSpacing: 0.7 },
  description: { lineHeight: 21 },
  example: { lineHeight: 18 },
});
